import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Landmark, Plus, Search, Edit, Trash2, Eye, X, CreditCard, Wallet, Building2 } from 'lucide-react';

interface Bank {
  id: number;
  name: string;
  accountNumber: string;
  ebanNumber: string;
  balance: number;
  address?: string;
  notes?: string;
  empId: number;
  companyId: number;
  statusId: number;
  creationDate: string;
  dateOfInActive?: string;
  branchId: number;
  departmentId?: number;
  isActive?: boolean;
}

export default function BanksPage() {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [form, setForm] = useState({
    name: '', accountNumber: '', ebanNumber: '', balance: '0',
    address: '', notes: '', empId: '1', companyId: '1',
    statusId: '1', branchId: '1', departmentId: ''
  });

  useEffect(() => { loadBanks(); }, []);

  const loadBanks = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/banks');
      if (res.data?.success) setBanks(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingBank(null);
    setForm({ name: '', accountNumber: '', ebanNumber: '', balance: '0', address: '', notes: '', empId: '1', companyId: '1', statusId: '1', branchId: '1', departmentId: '' });
    setShowModal(true);
  };

  const openEdit = (b: Bank) => {
    setEditingBank(b);
    setForm({
      name: b.name || '',
      accountNumber: b.accountNumber || '',
      ebanNumber: b.ebanNumber || '',
      balance: b.balance.toString(),
      address: b.address || '',
      notes: b.notes || '',
      empId: b.empId.toString(),
      companyId: b.companyId.toString(),
      statusId: b.statusId.toString(),
      branchId: b.branchId.toString(),
      departmentId: b.departmentId ? b.departmentId.toString() : ''
    });
    setShowModal(true);
  };

  const openView = (b: Bank) => { setSelectedBank(b); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        accountNumber: form.accountNumber,
        ebanNumber: form.ebanNumber,
        balance: parseFloat(form.balance) || 0,
        address: form.address,
        notes: form.notes,
        empId: parseInt(form.empId) || 1,
        companyId: parseInt(form.companyId) || 1,
        statusId: parseInt(form.statusId) || 1,
        branchId: parseInt(form.branchId) || 1,
        departmentId: form.departmentId ? parseInt(form.departmentId) : null
      };
      if (editingBank) {
        await api.put('/api/banks/' + editingBank.id, payload);
        alert("تم تحديث البنك بنجاح");
      } else {
        await api.post('/api/banks', payload);
        alert("تم إضافة البنك بنجاح");
      }
      setShowModal(false);
      loadBanks();
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف البنك؟")) return;
    try {
      await api.delete('/api/banks/' + id);
      loadBanks();
      alert("تم الحذف بنجاح");
    } catch (err: any) {
      alert(err.response?.data?.message || "حدث خطأ");
    }
  };

  const filtered = banks.filter(b =>
    (b.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.accountNumber || '').includes(search)
  );

  const totalBalance = banks.reduce((sum, b) => sum + (b.balance || 0), 0);
  const activeBanks = banks.filter(b => !b.dateOfInActive).length;
  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition";

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <Landmark className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>البنوك</span>
            <p className="text-sm text-gray-500 font-normal mt-1">إدارة البنوك والحسابات البنكية</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة بنك جديد</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Landmark className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">إجمالي البنوك</p>
          <p className="text-2xl font-bold text-gray-900">{banks.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CreditCard className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">البنوك النشطة</p>
          <p className="text-2xl font-bold text-green-700">{activeBanks}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><Wallet className="w-5 h-5 text-orange-600" /></div></div>
          <p className="text-sm text-gray-500">إجمالي الأرصدة</p>
          <p className="text-2xl font-bold text-orange-700">{totalBalance.toLocaleString()} ج.م</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="بحث باسم البنك أو رقم الحساب..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div><p className="text-gray-500 mt-4">جاري التحميل...</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Landmark className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد بنوك</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم البنك</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">رقم الحساب</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">IBAN</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الرصيد</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((bank) => (
                    <tr key={bank.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white"><Landmark className="w-5 h-5" /></div>
                          <span className="font-semibold text-gray-900">{bank.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className="font-mono text-sm text-gray-700">{bank.accountNumber}</span></td>
                      <td className="py-4 px-5"><span className="font-mono text-xs text-gray-500">{bank.ebanNumber}</span></td>
                      <td className="py-4 px-5"><span className="font-bold text-green-600">{bank.balance.toLocaleString()} ج.م</span></td>
                      <td className="py-4 px-5"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${!bank.dateOfInActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{!bank.dateOfInActive ? 'نشط' : 'غير نشط'}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openView(bank)} className="p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition" title="عرض"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(bank)} className="p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition" title="تعديل"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(bank.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title="حذف"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingBank ? 'تعديل البنك' : 'إضافة بنك جديد'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>اسم البنك *</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} /></div>
                <div><Label>رقم الحساب *</Label><Input value={form.accountNumber} onChange={(e: any) => setForm({...form, accountNumber: e.target.value})} required className={inputClass} /></div>
                <div><Label>IBAN *</Label><Input value={form.ebanNumber} onChange={(e: any) => setForm({...form, ebanNumber: e.target.value})} required className={inputClass} /></div>
                <div><Label>الرصيد</Label><Input type="number" step="0.01" value={form.balance} onChange={(e: any) => setForm({...form, balance: e.target.value})} className={inputClass} /></div>
                <div><Label>العنوان</Label><Input value={form.address} onChange={(e: any) => setForm({...form, address: e.target.value})} className={inputClass} /></div>
                <div><Label>معرف الموظف</Label><Input type="number" value={form.empId} onChange={(e: any) => setForm({...form, empId: e.target.value})} className={inputClass} /></div>
                <div><Label>معرف الشركة</Label><Input type="number" value={form.companyId} onChange={(e: any) => setForm({...form, companyId: e.target.value})} className={inputClass} /></div>
                <div><Label>معرف الفرع</Label><Input type="number" value={form.branchId} onChange={(e: any) => setForm({...form, branchId: e.target.value})} className={inputClass} /></div>
                <div><Label>معرف القسم</Label><Input type="number" value={form.departmentId} onChange={(e: any) => setForm({...form, departmentId: e.target.value})} className={inputClass} /></div>
                <div><Label>حالة الحساب</Label><Input type="number" value={form.statusId} onChange={(e: any) => setForm({...form, statusId: e.target.value})} className={inputClass} /></div>
                <div className="md:col-span-2"><Label>ملاحظات</Label><Input value={form.notes} onChange={(e: any) => setForm({...form, notes: e.target.value})} className={inputClass} /></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">{editingBank ? 'تحديث' : 'إضافة'}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedBank && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">تفاصيل البنك</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white"><Landmark className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedBank.name}</h3><p className="text-gray-500">حساب بنكي</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">رقم الحساب</p><p className="font-semibold font-mono">{selectedBank.accountNumber}</p></div>
              <div><p className="text-sm text-gray-500">IBAN</p><p className="font-semibold font-mono text-sm">{selectedBank.ebanNumber}</p></div>
              <div><p className="text-sm text-gray-500">الرصيد</p><p className="font-bold text-green-600 text-xl">{selectedBank.balance.toLocaleString()} ج.م</p></div>
              <div><p className="text-sm text-gray-500">الحالة</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${!selectedBank.dateOfInActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{!selectedBank.dateOfInActive ? 'نشط' : 'غير نشط'}</span></div>
              <div className="col-span-2"><p className="text-sm text-gray-500">العنوان</p><p className="font-semibold">{selectedBank.address || '-'}</p></div>
              <div><p className="text-sm text-gray-500">تاريخ الإنشاء</p><p className="font-semibold">{new Date(selectedBank.creationDate).toLocaleDateString('ar-EG')}</p></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedBank); }} className="flex-1 bg-blue-600 hover:bg-blue-700"><Edit className="w-4 h-4 ml-2" />تعديل</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
